import React from 'react';
import { Helmet } from 'react-helmet-async';

export type PdpSchemaOffer = Readonly<{
  /** Per-unit list price in major units, already formatted for schema.org (e.g. "1.39"). */
  price: string;
  /** ISO 4217 code the product is priced in. */
  priceCurrency: string;
  /** The pre-sale list price in major units, when `price` is a sale price; omitted otherwise. */
  listPrice?: string;
}>;

type PdpSchemaProps = Readonly<{
  name: string;
  description: string;
  canonicalURL: string;
  imageUrl?: string;
  /** The template id: one SKU per size, the same key the product feed uses. */
  sku: string;
  offer: PdpSchemaOffer;
}>;

/**
 * The Product structured data a card product page publishes for Google Shopping and rich
 * results: one Product with one Offer, keyed to the canonical URL. Google checks the feed price
 * against this block, so the price here is the price the page shows (see `getPdpSeoOffer`): the
 * list price, or the sale price while a promotion runs, with the list price alongside as a
 * `ListPrice` specification so the strikethrough can be shown. Never a display-currency conversion.
 *
 * Rendered only when the page has a sellable product with a price (`offer`): a digital-only
 * design has nothing to offer here, and a Product without an Offer earns no listing.
 */
/**
 * JSON-LD inside a `<script>` is raw text, not HTML: browsers and crawlers do not decode entities
 * there, and a `</script>` inside a data-derived string (the design name, say) would close the
 * block and hand the rest to the HTML parser. So the JSON is serialised once and the characters
 * that could end the script or start a tag are written as JSON escapes, which every JSON parser
 * reads back as the original characters.
 */
const toScriptSafeJson = (value: unknown): string =>
  JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

export const PdpSchema = ({ name, description, canonicalURL, imageUrl, sku, offer }: PdpSchemaProps) => {
  const product = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${canonicalURL}#product`,
    name,
    description,
    ...(imageUrl ? { image: [imageUrl] } : {}),
    sku,
    brand: { '@type': 'Brand', name: 'Joy' },
    url: canonicalURL,
    offers: {
      '@type': 'Offer',
      url: canonicalURL,
      price: offer.price,
      priceCurrency: offer.priceCurrency,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      ...(offer.listPrice
        ? {
            priceSpecification: [{ '@type': 'UnitPriceSpecification', priceType: 'https://schema.org/ListPrice', price: offer.listPrice, priceCurrency: offer.priceCurrency }],
          }
        : {}),
    },
  };

  // Helmet emits a script's text child verbatim on the server and sets it as innerHTML on the
  // client, with no HTML escaping either way (and it ignores `dangerouslySetInnerHTML`), so the
  // JSON must already be safe to sit inside a script: see `toScriptSafeJson`.
  return (
    <Helmet>
      <script type="application/ld+json">{toScriptSafeJson(product)}</script>
    </Helmet>
  );
};
